import type {
  LearningPositionCoverageStat,
  LearningPositionSpineCoverageNode,
  LearningPositionSpineCoverageSummary,
} from './reviewService';
import type { ParticleCoverageSubjectEntry } from './particleCoverageService';

export type SpineCoveragePageCache = {
  updatedAt: string;
  nodes: LearningPositionSpineCoverageNode[];
  unmappedBoundCount: number;
  unmappedReviewedCount: number;
  mappedBoundCount: number;
  boundParticleCount: number;
  spineReviewedCount: number;
  subjectCoverage: ParticleCoverageSubjectEntry | null;
  uploadedTrunkCoverage: LearningPositionCoverageStat | null;
  summary?: LearningPositionSpineCoverageSummary | null;
};

const PREFIX = 'spine-coverage-page-v1:';

function storageKey(userId: string, subject: string) {
  return `${PREFIX}${userId}:${subject}`;
}

export function peekSpineCoveragePageCache(
  userId: string,
  subject: string,
): SpineCoveragePageCache | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId, subject));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SpineCoveragePageCache;
    if (!parsed?.updatedAt || !Array.isArray(parsed.nodes)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeSpineCoveragePageCache(
  userId: string,
  subject: string,
  payload: Omit<SpineCoveragePageCache, 'updatedAt'> & { updatedAt?: string },
): SpineCoveragePageCache {
  const next: SpineCoveragePageCache = {
    ...payload,
    updatedAt: payload.updatedAt || new Date().toISOString(),
  };
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(storageKey(userId, subject), JSON.stringify(next));
    } catch {
      // ignore quota
    }
  }
  return next;
}

export function formatSpineCoverageCacheUpdatedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
